import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Users,
  Settings,
  Plus,
  Trash2,
  Shield,
  CheckCircle,
  AlertCircle,
  Loader2,
  Mail,
  MessageCircle,
  Crown,
  User,
  X,
  Link,
  Copy,
  ExternalLink,
  Globe,
  DollarSign,
  Save,
  Upload,
  Warehouse,
  ChevronDown,
} from 'lucide-react';
import { organizationService, VisibilityRule } from '../../../services/OrganizationService';
import { settingsService } from '../../../services/SettingsService';
import { parseWarehouseCsv, mergeWarehouses } from '../../../utils/warehouseCsv';
import AppLayout from '../../../layout/AppLayout';
import MobileHeader from '../../../layout/MobileHeader';
import OrgWhatsAppPanel from './OrgWhatsAppPanel';

interface Organization {
  id: number;
  name: string;
  description?: string;
  role: string;
  is_admin: boolean;
  alias: string;
}

interface Member {
  id: number;
  uid: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  joined_at: string;
  permissions?: { rules?: VisibilityRule[]; home_city?: string | null; home_warehouse?: string | null };
}

/** Org warehouse. `code` is the DMS stock-location id (Codigo_Bodega) and is what
 *  member visibility rules on the DMS location field match against. */
interface OrgWarehouse { code: string; name: string; abbrev: string }
const EMPTY_WAREHOUSE: OrgWarehouse = { code: '', name: '', abbrev: '' };

/** Known rule targets. Adding a restriction tomorrow = one more entry here (or a custom path). */
const RULE_FIELDS: { path: string; labelKey: string; label: string }[] = [
  { path: 'integration_data.dms_data[].location', labelKey: 'organization.rules.fieldDmsLocation', label: 'Location Rules' },
];
// New rules default to "show by priority": the safe choice, since it never hides stock.
const DEFAULT_RULE: VisibilityRule = { field: RULE_FIELDS[0].path, mode: 'prioritize', values: [] };

/** Org-admin control: rule-based limits on what one member sees (inline editor). */
const MemberRules: React.FC<{
  member: Member;
  warehouses: OrgWarehouse[];
  onSave: (uid: string, rules: VisibilityRule[], homeCity: string | null) => Promise<void>;
}> = ({ member, warehouses, onSave }) => {
  const { t } = useTranslation();
  const rules = member.permissions?.rules ?? [];
  // Home city: every org warehouse in that city counts as "home" (the cart picks
  // the cheapest in-stock one). Members set before cities existed may still
  // carry a single legacy home_warehouse — shown as that warehouse's city.
  const cityOf = (w: OrgWarehouse) => w.name.trim() || w.abbrev.trim();
  const legacyHome = member.permissions?.home_warehouse ?? null;
  const legacyCity = legacyHome ? (warehouses.find(w => w.code.toUpperCase() === legacyHome.toUpperCase()) ?? null) : null;
  const homeCity = member.permissions?.home_city ?? (legacyCity ? cityOf(legacyCity) : null) ?? (legacyHome || null);
  const cities = Array.from(new Map(warehouses.map(w => [cityOf(w).toLowerCase(), cityOf(w)] as const)).values())
    .filter(Boolean).sort((a, b) => a.localeCompare(b));
  const cityWarehouses = (c: string) => warehouses.filter(w => cityOf(w).toLowerCase() === c.toLowerCase()).map(w => w.code);
  const homeKnown = !homeCity || cities.some(c => c.toLowerCase() === homeCity.toLowerCase());
  const [drafts, setDrafts] = useState<string[]>(rules.map(() => ''));
  const [customPath, setCustomPath] = useState<string[]>(rules.map(() => ''));
  // Rule edits leave the home city untouched (null = unchanged); clearing sends ''.
  const save = (next: VisibilityRule[]) => onSave(member.uid, next, null);
  const saveHome = (city: string | null) => onSave(member.uid, rules, city ?? '');
  const patch = (i: number, p: Partial<VisibilityRule>) => save(rules.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const isPreset = (f: string) => RULE_FIELDS.some(x => x.path === f);
  // The DMS location rule matches warehouse codes, so offer the org's defined
  // warehouses as a picker instead of making admins retype the codes.
  const isWarehouseField = (f: string) => f === RULE_FIELDS[0].path;
  const findWarehouse = (code: string) => warehouses.find(x => x.code.toUpperCase() === code.toUpperCase());
  const warehouseLabel = (code: string) => {
    const w = findWarehouse(code);
    return w && (w.name || w.abbrev) ? `${code} · ${w.name || w.abbrev}` : code;
  };
  const addValue = (i: number, raw: string) => {
    const v = raw.trim();
    if (v && !rules[i].values.includes(v)) patch(i, { values: [...rules[i].values, v] });
    setDrafts(d => { const n = [...d]; n[i] = ''; return n; });
  };
  // Same field styling as the locale / warehouses forms.
  const fieldCls = 'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white';
  const labelCls = 'block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1.5';
  const modeHint: Record<VisibilityRule['mode'], string> = {
    include: t('organization.rules.includeHint', 'The member only sees stock from these warehouses.'),
    exclude: t('organization.rules.excludeHint', 'The member sees stock from every warehouse except these.'),
    prioritize: t('organization.rules.prioritizeHint', 'Listed first, in this order. Other values stay visible after them, so the member still sees where else the part is available.'),
  };
  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      {/* Home city — the member's default point of sale. Every warehouse in the
          city counts as home; the server folds them into a "home first" priority
          rule and the cart prices from the cheapest in-stock one. */}
      <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
        <label className={labelCls}>{t('organization.rules.homeCity', 'Home city')}</label>
        {cities.length > 0 ? (
          <select
            value={homeKnown ? (homeCity ?? '') : '__unknown__'}
            onChange={e => saveHome(e.target.value && e.target.value !== '__unknown__' ? e.target.value : null)}
            className={`${fieldCls} sm:max-w-md`}
          >
            <option value="">{t('organization.rules.homeCityNone', 'None — no default warehouse')}</option>
            {!homeKnown && <option value="__unknown__">{homeCity}</option>}
            {cities.map(c => {
              const codes = cityWarehouses(c);
              return <option key={c} value={c}>{codes.length > 1 ? `${c} (${codes.length} ${t('organization.rules.warehousesShort', 'warehouses')})` : c}</option>;
            })}
          </select>
        ) : (
          <p className="text-sm text-gray-500">{t('organization.rules.homeWarehouseNoList', 'Define warehouses in the Warehouses tab first.')}</p>
        )}
        {homeCity && homeKnown && cityWarehouses(homeCity).length > 0 && (
          <p className="mt-1.5 text-xs text-gray-600 font-mono">{cityWarehouses(homeCity).join(' · ')}</p>
        )}
        <p className="mt-1.5 text-xs text-gray-500">
          {t('organization.rules.homeCityHint', 'Results list this city\'s warehouses first and the cart prices from the cheapest one with stock by default. Other warehouses stay visible and selectable.')}
        </p>
      </div>

      <div className="mb-4">
        <p className="text-sm font-medium text-gray-900">{t('organization.rules.title', 'Visibility rules — what this member can see')}</p>
        <p className="mt-0.5 text-xs text-gray-500">
          {t('organization.rules.description', 'Limit which stock locations this member sees in search and cart results. With no rules, they see everything.')}
        </p>
      </div>

      {rules.length === 0 && (
        <div className="px-4 py-5 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-center">
          <p className="text-sm text-gray-600">{t('organization.rules.none', 'No limits. This member sees everything.')}</p>
        </div>
      )}

      <div className="space-y-3">
        {rules.map((r, i) => (
          <div key={i} className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                <div>
                  <label className={labelCls}>{t('organization.rules.field', 'Field')}</label>
                  <select
                    value={isPreset(r.field) ? r.field : '__custom__'}
                    onChange={e => {
                      if (e.target.value === '__custom__') setCustomPath(c => { const n = [...c]; n[i] = r.field; return n; });
                      else { patch(i, { field: e.target.value }); setCustomPath(c => { const n = [...c]; n[i] = ''; return n; }); }
                    }}
                    className={fieldCls}
                  >
                    {RULE_FIELDS.map(f => <option key={f.path} value={f.path}>{t(f.labelKey, f.label)}</option>)}
                    <option value="__custom__">{t('organization.rules.customField', 'Custom path…')}</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t('organization.rules.mode', 'Rule')}</label>
                  <select value={r.mode} onChange={e => patch(i, { mode: e.target.value as VisibilityRule['mode'] })} className={fieldCls}>
                    <option value="include">{t('organization.rules.include', 'Show only these locations')}</option>
                    <option value="exclude">{t('organization.rules.exclude', 'Show all except these')}</option>
                    <option value="prioritize">{t('organization.rules.prioritize', 'Show by priority')}</option>
                  </select>
                </div>
                {(!isPreset(r.field) || customPath[i]) && (
                  <div className="sm:col-span-2">
                    <label className={labelCls}>{t('organization.rules.customPath', 'Custom path')}</label>
                    <input
                      value={customPath[i] || r.field}
                      onChange={e => setCustomPath(c => { const n = [...c]; n[i] = e.target.value; return n; })}
                      onBlur={() => { const v = (customPath[i] || '').trim(); if (v && v !== r.field) patch(i, { field: v }); }}
                      placeholder="integration_data.dms_data[].location"
                      className={`${fieldCls} font-mono text-xs`}
                    />
                    <p className="mt-1 text-xs text-gray-500">{t('organization.rules.customPathHint', 'Dot path inside a cart item; the list segment ends in [].')}</p>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { save(rules.filter((_, j) => j !== i)); setDrafts([]); setCustomPath([]); }}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all shrink-0"
                title={t('organization.rules.remove', 'Remove rule')}
                aria-label={t('organization.rules.remove', 'Remove rule')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div>
              <label className={labelCls}>
                {isWarehouseField(r.field) ? t('organization.rules.warehouses', 'Warehouses') : t('organization.rules.values', 'Values')}
              </label>
              {r.values.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  {r.values.map((v, k) => (
                    <span key={v} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-900">
                      {r.mode === 'prioritize' && <span className="font-medium text-gray-500">{k + 1}.</span>}
                      <span className={isWarehouseField(r.field) ? 'font-mono' : ''}>{isWarehouseField(r.field) ? warehouseLabel(v) : v}</span>
                      {r.mode === 'prioritize' && k > 0 && (
                        <button
                          type="button"
                          onClick={() => { const n = [...r.values]; [n[k - 1], n[k]] = [n[k], n[k - 1]]; patch(i, { values: n }); }}
                          className="px-1 text-gray-500 hover:text-gray-900 rounded"
                          title={t('organization.rules.moveUp', 'Move up')}
                          aria-label={t('organization.rules.moveUp', 'Move up')}
                        >↑</button>
                      )}
                      <button
                        type="button"
                        onClick={() => patch(i, { values: r.values.filter(x => x !== v) })}
                        className="p-0.5 text-gray-500 hover:text-red-600 rounded-full"
                        aria-label={t('organization.rules.removeValue', 'Remove')}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                {isWarehouseField(r.field) && warehouses.length > 0 && (
                  <select
                    value=""
                    onChange={e => { if (e.target.value) addValue(i, e.target.value); }}
                    className={`${fieldCls} sm:max-w-xs`}
                    aria-label={t('organization.rules.pickWarehouse', 'Add warehouse…')}
                  >
                    <option value="">{t('organization.rules.pickWarehouse', 'Add warehouse…')}</option>
                    {warehouses.filter(w => !r.values.includes(w.code)).map(w => (
                      <option key={w.code} value={w.code}>{warehouseLabel(w.code)}</option>
                    ))}
                  </select>
                )}
                <form
                  className="flex gap-2 flex-1"
                  onSubmit={e => { e.preventDefault(); addValue(i, drafts[i] || ''); }}
                >
                  <input
                    value={drafts[i] || ''}
                    onChange={e => setDrafts(d => { const n = [...d]; n[i] = e.target.value; return n; })}
                    placeholder={isWarehouseField(r.field)
                      ? t('organization.rules.addCodePlaceholder', 'Or type a warehouse ID, e.g. PV412')
                      : t('organization.rules.addValue', 'Add value')}
                    className={`${fieldCls} ${isWarehouseField(r.field) ? 'font-mono' : ''}`}
                  />
                  <button
                    type="submit"
                    disabled={!(drafts[i] || '').trim()}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:border-gray-400 text-gray-900 transition disabled:opacity-50 shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                    {t('organization.rules.addValueButton', 'Add')}
                  </button>
                </form>
              </div>
              <p className="mt-2 text-xs text-gray-500">{modeHint[r.mode]}</p>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => save([...rules, DEFAULT_RULE])}
        className="mt-3 flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline"
      >
        <Plus className="w-4 h-4" />
        {t('organization.rules.add', 'Add rule')}
      </button>
    </div>
  );
};

interface OrganizationSettingsPanelProps {
  onBack: () => void;
  asModal?: boolean;
}

type OrgTab = 'info' | 'warehouses' | 'members' | 'channels';

/** Map a ?tab= value onto the tab that owns that content today.
 *  The panel had five tabs; 'settings' (language, currency, logo) folded into
 *  Info and 'guest' into Channels, so old links must not land on a dead tab. */
const resolveOrgTab = (raw: string | null): OrgTab =>
  raw === 'members' ? 'members'
    : raw === 'channels' || raw === 'guest' ? 'channels'
      : raw === 'warehouses' ? 'warehouses'
        : 'info';

const OrganizationSettingsPanel: React.FC<OrganizationSettingsPanelProps> = ({ onBack, asModal = false }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [rulesFor, setRulesFor] = useState<string | null>(null);
  const [orgSettings, setOrgSettings] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTabState] = useState<OrgTab>(
    () => resolveOrgTab(searchParams.get('tab'))
  );

  const setActiveTab = (tab: OrgTab) => {
    setActiveTabState(tab);
    // setSearchParams(prev => { prev.set('tab', tab); return prev; }, { replace: true });
  };

  // Form states
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgDescription, setNewOrgDescription] = useState('');
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  // Guest invite state
  const [guestInvites, setGuestInvites] = useState<any[]>([]);
  const [guestInvitesLoading, setGuestInvitesLoading] = useState(false);
  const [newInviteLabel, setNewInviteLabel] = useState('');
  const [newInviteMaxUses, setNewInviteMaxUses] = useState<string>('');
  const [newInviteExpiresHours, setNewInviteExpiresHours] = useState<string>('');
  const [newInviteIconUrl, setNewInviteIconUrl] = useState<string>('');
  const [newInviteHeaderText, setNewInviteHeaderText] = useState<string>('');
  const [newInviteAllowedDomain, setNewInviteAllowedDomain] = useState<string>('');
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // Locale settings form state (language + currency)
  const [localeLanguage, setLocaleLanguage] = useState('en');
  const [localeCurrency, setLocaleCurrency] = useState('USD');
  const [vatRate, setVatRate] = useState<string>('');
  const [localeCountry, setLocaleCountry] = useState('');
  const [numberFormat, setNumberFormat] = useState<string>('comma');
  // Cart availability display config
  const [inventorySourcePriority, setInventorySourcePriority] = useState<string>('dms_first');
  const [inventoryShowAlternative, setInventoryShowAlternative] = useState<boolean>(true);
  const [inventoryShowImporterQty, setInventoryShowImporterQty] = useState<boolean>(false);
  const [inventoryPricePriority, setInventoryPricePriority] = useState<string>('original_first');
  // Which warehouse prices a line by default (home → lowest / home → first / lowest).
  const [inventoryLocationPriority, setInventoryLocationPriority] = useState<string>('list_order');
  // Delivery time (spec §6.2) — tenant zone table + default fallback. Values are
  // stored as strings for the numeric inputs; coerced to ints on save.
  const [deliveryDefaultDays, setDeliveryDefaultDays] = useState<string>('5');
  // Order alerts (lifecycle SLA) — per-org thresholds; null column = defaults.
  const [slaEnabled, setSlaEnabled] = useState<boolean>(false);
  const [slaReviewHours, setSlaReviewHours] = useState<string>('4');
  const [slaCartHours, setSlaCartHours] = useState<string>('24');
  const [slaReminderHours, setSlaReminderHours] = useState<string>('24');
  const [slaEscalateHours, setSlaEscalateHours] = useState<string>('48');
  const [slaWindowDays, setSlaWindowDays] = useState<string>('7');
  const [deliveryHomeCityLabel, setDeliveryHomeCityLabel] = useState<string>('Santiago');
  const [deliveryHomeCityDays, setDeliveryHomeCityDays] = useState<string>('3');
  const [deliveryOutsideDays, setDeliveryOutsideDays] = useState<string>('5');
  const [deliveryInternationalDays, setDeliveryInternationalDays] = useState<string>('45');
  const [replyEmail, setReplyEmail] = useState<string>('');
  const [isSavingLocale, setIsSavingLocale] = useState(false);
  // Warehouses tab — org-defined stock locations (see OrgWarehouse).
  const [warehouses, setWarehouses] = useState<OrgWarehouse[]>([]);
  const [warehousesOpen, setWarehousesOpen] = useState(true);
  const patchWarehouse = (i: number, p: Partial<OrgWarehouse>) =>
    setWarehouses(ws => ws.map((w, j) => (j === i ? { ...w, ...p } : w)));
  // CSV import fills the list in place (upsert by code); the admin still clicks Save.
  const handleWarehouseCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const rows = parseWarehouseCsv(await file.text());
    if (!rows.length) {
      showNotification(t('organization.error', 'Error'), t('organization.warehouses.csvEmpty', 'No warehouses found in the file. It needs a code column (e.g. Codigo_Bodega).'), 'error');
      return;
    }
    setWarehousesOpen(true);
    setWarehouses(ws => mergeWarehouses(ws.filter(w => w.code.trim() || w.name.trim() || w.abbrev.trim()), rows));
    showNotification(t('organization.success', 'Success'), t('organization.warehouses.csvImported', '{{n}} warehouses imported. Click Save to keep them.', { n: rows.length }), 'success');
  };

  // Logo state
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });

  useEffect(() => {
    loadOrganizationData();
    if (activeTab === 'channels') {
      loadGuestInvites();
    }
  }, []);

  // ── Guest invite helpers ─────────────────────
  const loadGuestInvites = async () => {
    setGuestInvitesLoading(true);
    try {
      const res = await organizationService.getGuestInvites();
      setGuestInvites(res.data.invites || []);
    } catch (e) {
      console.error('Failed to load guest invites:', e);
    } finally {
      setGuestInvitesLoading(false);
    }
  };

  const handleCreateInvite = async () => {
    setIsCreatingInvite(true);
    try {
      await organizationService.createGuestInvite({
        label: newInviteLabel || undefined,
        max_uses: newInviteMaxUses ? parseInt(newInviteMaxUses) : undefined,
        expires_in_hours: newInviteExpiresHours ? parseInt(newInviteExpiresHours) : undefined,
        icon_url: newInviteIconUrl || undefined,
        header_text: newInviteHeaderText || undefined,
        allowed_domain: newInviteAllowedDomain || undefined,
      });
      setNewInviteLabel('');
      setNewInviteMaxUses('');
      setNewInviteExpiresHours('');
      setNewInviteIconUrl('');
      setNewInviteHeaderText('');
      setNewInviteAllowedDomain('');
      await loadGuestInvites();
      showNotification(t('organization.success', 'Success'), t('organization.guest.inviteCreated', 'Invite link created!'), 'success');
    } catch (e: any) {
      showNotification(t('organization.error', 'Error'), e?.response?.data?.detail || t('organization.guest.failedCreate', 'Failed to create invite'), 'error');
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const handleIconFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingIcon(true);
    try {
      const res = await organizationService.uploadGuestInviteIcon(file);
      setNewInviteIconUrl(res.data.url);
    } catch (err: any) {
      showNotification(t('organization.error', 'Error'), err?.response?.data?.detail || t('organization.guest.iconUploadFailed', 'Failed to upload icon'), 'error');
    } finally {
      setIsUploadingIcon(false);
      e.target.value = '';
    }
  };

  const handleRevokeInvite = async (token: string) => {
    try {
      await organizationService.revokeGuestInvite(token);
      setGuestInvites(prev => prev.map(i => i.token === token ? { ...i, is_active: false } : i));
      showNotification(t('organization.success', 'Success'), t('organization.guest.inviteRevoked', 'Invite revoked.'), 'success');
    } catch (e: any) {
      showNotification(t('organization.error', 'Error'), e?.response?.data?.detail || t('organization.guest.failedRevoke', 'Failed to revoke invite'), 'error');
    }
  };

  const copyInviteLink = (token: string) => {
    const url = `${window.location.origin}/guest/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    });
  };

  const guestLink = (token: string) => `${window.location.origin}/guest/${token}`;
  // ─────────────────────────────────────────────

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingLogo(true);
    try {
      const res = await organizationService.uploadOrganizationLogo(file);
      setLogoUrl(res.data.logo_url);
      showNotification(t('organization.success', 'Success'), t('organization.logoUploaded', 'Logo uploaded successfully'), 'success');
    } catch (err: any) {
      showNotification(t('organization.error', 'Error'), err?.response?.data?.detail || t('organization.logoUploadError', 'Failed to upload logo'), 'error');
    } finally {
      setIsUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    setIsUploadingLogo(true);
    try {
      await organizationService.deleteOrganizationLogo();
      setLogoUrl(null);
      showNotification(t('organization.success', 'Success'), t('organization.logoRemoved', 'Logo removed'), 'success');
    } catch (err: any) {
      showNotification(t('organization.error', 'Error'), err?.response?.data?.detail || t('organization.logoRemoveError', 'Failed to remove logo'), 'error');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSaveLocale = async (successMessage?: string) => {
    if (!orgSettings) return;
    setIsSavingLocale(true);
    try {
      const currentCatalogs = orgSettings.catalogs_config || [];
      const deliveryDefaultDaysNum = Math.max(0, parseInt(deliveryDefaultDays, 10) || 5);
      const num = (v: string, d: number) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0 ? n : d; };
      const orderSlaPayload = {
        enabled: slaEnabled,
        review_hours: num(slaReviewHours, 4),
        cart_hours: num(slaCartHours, 24),
        reminder_hours: num(slaReminderHours, 24),
        escalate_hours: num(slaEscalateHours, 48),
        window_days: num(slaWindowDays, 7),
      };
      // Blank rows are dropped; the server also dedupes codes.
      const warehousesPayload = warehouses
        .map(w => ({ code: w.code.trim(), name: w.name.trim(), abbrev: w.abbrev.trim().toUpperCase() }))
        .filter(w => w.code);
      const deliveryZonesPayload = {
        home_city_label: deliveryHomeCityLabel.trim() || 'Santiago',
        home_city_days: Math.max(0, parseInt(deliveryHomeCityDays, 10) || 0),
        outside_days: Math.max(0, parseInt(deliveryOutsideDays, 10) || 0),
        international_days: Math.max(0, parseInt(deliveryInternationalDays, 10) || 0),
      };
      await organizationService.updateOrganizationSettings({
        language: localeLanguage,
        currency: localeCurrency,
        catalogs_config: currentCatalogs,
        vat_rate: vatRate !== '' ? parseFloat(vatRate) : null,
        country: localeCountry || null,
        number_format: numberFormat,
        reply_email: replyEmail || null,
        inventory_source_priority: inventorySourcePriority,
        inventory_show_alternative: inventoryShowAlternative,
        inventory_show_importer_qty: inventoryShowImporterQty,
        inventory_price_priority: inventoryPricePriority,
        inventory_location_priority: inventoryLocationPriority,
        delivery_default_days: deliveryDefaultDaysNum,
        delivery_zones: deliveryZonesPayload,
        order_sla_config: orderSlaPayload,
        warehouses: warehousesPayload,
      });
      setOrgSettings((prev: any) => ({ ...prev, language: localeLanguage, currency: localeCurrency, vat_rate: vatRate !== '' ? parseFloat(vatRate) : null, country: localeCountry || null, number_format: numberFormat, reply_email: replyEmail || null, inventory_source_priority: inventorySourcePriority, inventory_show_alternative: inventoryShowAlternative, inventory_show_importer_qty: inventoryShowImporterQty, inventory_price_priority: inventoryPricePriority, inventory_location_priority: inventoryLocationPriority, delivery_default_days: deliveryDefaultDaysNum, delivery_zones: deliveryZonesPayload, warehouses: warehousesPayload }));
      setWarehouses(warehousesPayload);

      // Propagate the new org language to all configured catalogs
      // (org language only affects catalog language, not the admin's personal UI language)
      await settingsService.setCatalogLanguage(localeLanguage);

      showNotification(
        t('organization.success', 'Success'),
        successMessage ?? t('organization.localeUpdated', 'Language and currency updated successfully'),
        'success'
      );
    } catch (error: any) {
      console.error('Failed to update locale settings:', error);
      showNotification(
        t('organization.error', 'Error'),
        error?.response?.data?.detail || t('organization.localeSaveError', 'Failed to update language and currency'),
        'error'
      );
    } finally {
      setIsSavingLocale(false);
    }
  };

  const loadOrganizationData = async () => {
    setIsLoading(true);
    try {
      const orgResponse = await organizationService.getOrganizationInfo();
      const orgData = orgResponse.data.organization;

      if (orgData) {
        setOrganization(orgData);

        // Load settings and members if user is part of an organization
        const [settingsResponse, membersResponse] = await Promise.all([
          organizationService.getOrganizationSettings(),
          orgData.is_admin ? organizationService.getOrganizationMembers() : Promise.resolve({ data: { members: [] } })
        ]);

        setOrgSettings(settingsResponse.data);
        setLocaleLanguage(settingsResponse.data.language || 'en');
        setLocaleCurrency(settingsResponse.data.currency || 'USD');
        setVatRate(settingsResponse.data.vat_rate != null ? String(settingsResponse.data.vat_rate) : '');
        setLocaleCountry(settingsResponse.data.country || '');
        setNumberFormat(settingsResponse.data.number_format || 'comma');
        setInventorySourcePriority(settingsResponse.data.inventory_source_priority || 'dms_first');
        setInventoryShowAlternative(settingsResponse.data.inventory_show_alternative !== false);
        setInventoryShowImporterQty(settingsResponse.data.inventory_show_importer_qty === true);
        setInventoryPricePriority(settingsResponse.data.inventory_price_priority || 'original_first');
        setInventoryLocationPriority(settingsResponse.data.inventory_location_priority || 'list_order');
        setDeliveryDefaultDays(settingsResponse.data.delivery_default_days != null ? String(settingsResponse.data.delivery_default_days) : '5');
        {
          const c = settingsResponse.data.order_sla_config || {};
          setSlaEnabled(c.enabled === true);
          setSlaReviewHours(c.review_hours != null ? String(c.review_hours) : '4');
          setSlaCartHours(c.cart_hours != null ? String(c.cart_hours) : '24');
          setSlaReminderHours(c.reminder_hours != null ? String(c.reminder_hours) : '24');
          setSlaEscalateHours(c.escalate_hours != null ? String(c.escalate_hours) : '48');
          setSlaWindowDays(c.window_days != null ? String(c.window_days) : '7');
        }
        {
          const z = settingsResponse.data.delivery_zones || {};
          setDeliveryHomeCityLabel(z.home_city_label ?? 'Santiago');
          setDeliveryHomeCityDays(z.home_city_days != null ? String(z.home_city_days) : '3');
          setDeliveryOutsideDays(z.outside_days != null ? String(z.outside_days) : '5');
          setDeliveryInternationalDays(z.international_days != null ? String(z.international_days) : '45');
        }
        setReplyEmail(settingsResponse.data.reply_email || '');
        setWarehouses(Array.isArray(settingsResponse.data.warehouses)
          ? settingsResponse.data.warehouses.map((w: any) => ({ code: String(w?.code ?? ''), name: String(w?.name ?? ''), abbrev: String(w?.abbrev ?? '') }))
          : []);
        setLogoUrl(settingsResponse.data.logo_url || null);
        if (orgData.is_admin) {
          setMembers(membersResponse.data.members || []);
        }
      }
    } catch (error: any) {
      console.error('Failed to load organization data:', error);
      if (error.response?.status !== 404) {
        showNotification(
          t('organization.error', 'Error'),
          t('organization.loadError', 'Failed to load organization data'),
          'error'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({ show: true, title, message, type });
    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 5000);
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim()) {
      showNotification(
        t('organization.error', 'Error'),
        t('organization.nameRequired', 'Organization name is required'),
        'error'
      );
      return;
    }

    setIsCreatingOrg(true);
    try {
      const response = await organizationService.createOrganization({
        name: newOrgName,
        description: newOrgDescription
      });

      showNotification(
        t('organization.success', 'Success'),
        t('organization.createSuccess', 'Organization created successfully'),
        'success'
      );

      // Reload organization data
      await loadOrganizationData();
      setNewOrgName('');
      setNewOrgDescription('');
    } catch (error) {
      console.error('Failed to create organization:', error);
      showNotification(
        t('organization.error', 'Error'),
        t('organization.createError', 'Failed to create organization'),
        'error'
      );
    } finally {
      setIsCreatingOrg(false);
    }
  };


  const handleSetMemberRules = async (memberUid: string, rules: VisibilityRule[], homeCity: string | null) => {
    try {
      const r = await organizationService.setMemberPermissions(memberUid, rules, homeCity);
      setMembers(ms => ms.map(m => (m.uid === memberUid ? { ...m, permissions: r.data.permissions } : m)));
    } catch (error: any) {
      showNotification(t('organization.error', 'Error'), error.response?.data?.detail || t('organization.rules.saveError', 'Failed to save rules'), 'error');
    }
  };

  const handleRemoveMember = async (memberUid: string) => {
    if (!window.confirm(t('organization.confirmRemove', 'Are you sure you want to remove this member?'))) {
      return;
    }

    setIsSaving(true);
    try {
      await organizationService.removeOrganizationMember(memberUid);

      showNotification(
        t('organization.success', 'Success'),
        t('organization.memberRemoved', 'Member removed successfully'),
        'success'
      );

      // Reload members
      const response = await organizationService.getOrganizationMembers();
      setMembers(response.data.members || []);
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      showNotification(
        t('organization.error', 'Error'),
        error.response?.data?.detail || t('organization.removeMemberError', 'Failed to remove member'),
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    const loadingContent = (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
          <span className="text-sm text-gray-600">{t('organization.loading', 'Loading...')}</span>
        </div>
      </div>
    );
    if (asModal) return loadingContent;
    return (
      <AppLayout pageTitle={t('organization.title', 'Organization')}>
        <div className="h-full flex flex-col bg-main-bg">
          <MobileHeader title={t('organization.title', 'Organization')} />
          {loadingContent}
        </div>
      </AppLayout>
    );
  }

  const innerContent = (
    <div className={asModal ? 'h-full flex flex-col' : 'h-full flex flex-col bg-main-bg'}>
      {!asModal && <MobileHeader title={t('organization.title', 'Organization')} />}

        {/* Content — full page, centered column (matches the Integrations page) */}
        <div className="flex-1 overflow-auto bg-main-bg min-h-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-8 sm:py-10">

            {/* Notification */}
            {notification.show && (
              <div className="mb-6">
                <div className={`p-4 rounded-xl border ${notification.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
                  notification.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
                    notification.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
                      'bg-blue-50 border-blue-200 text-blue-800'
                  }`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{notification.title}</p>
                      <p className="text-sm mt-0.5 opacity-90">{notification.message}</p>
                    </div>
                    <button
                      onClick={() => setNotification(prev => ({ ...prev, show: false }))}
                      className="text-current opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            {!organization ? (
              /* Create Organization View */
              <div className="bg-white rounded-xl border border-gray-200 p-8 animate-fadeIn">
                <div className="max-w-2xl mx-auto">
                  <div className="text-center mb-8">
                    <div className="inline-flex p-4 bg-gray-100 rounded-2xl mb-4">
                      <Building2 className="w-12 h-12 text-gray-600" />
                    </div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                      {t('organization.createTitle', 'Create Your Organization')}
                    </h2>
                    <p className="text-gray-600">
                      {t('organization.createDescription', 'Set up an organization to share catalog configurations with your team')}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        {t('organization.name', 'Organization Name')}
                      </label>
                      <input
                        type="text"
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        placeholder={t('organization.namePlaceholder', 'e.g., Auto Repair Shop')}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 transition-all"
                        disabled={isCreatingOrg}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        {t('organization.description', 'Description')} ({t('common.optional', 'Optional')})
                      </label>
                      <textarea
                        value={newOrgDescription}
                        onChange={(e) => setNewOrgDescription(e.target.value)}
                        placeholder={t('organization.descriptionPlaceholder', 'What does your organization do?')}
                        rows={3}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 transition-all resize-none"
                        disabled={isCreatingOrg}
                      />
                    </div>

                    <button
                      onClick={handleCreateOrganization}
                      disabled={isCreatingOrg || !newOrgName.trim()}
                      className="w-full py-3 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                    >
                      {isCreatingOrg ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('organization.creating', 'Creating...')}
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          {t('organization.createButton', 'Create Organization')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Organization Management View — centered column, horizontal tabs */
              <div className="animate-fadeIn">
                <div>
                  <div>
                    {/* Horizontal pill tab nav (matches the Integrations page) */}
                    <nav className="flex gap-2 mb-8 pb-4 border-b border-gray-200 overflow-x-auto">
                      {([
                        { id: 'info' as const, icon: Building2, label: t('organization.tabs.info', 'Info'), adminOnly: false },
                        { id: 'warehouses' as const, icon: Warehouse, label: t('organization.tabs.warehouses', 'Warehouses'), adminOnly: true },
                        { id: 'members' as const, icon: Users, label: t('organization.tabs.members', 'Members'), adminOnly: true },
                        { id: 'channels' as const, icon: MessageCircle, label: t('organization.tabs.channels', 'Channels'), adminOnly: true },
                      ]).filter(tab => !tab.adminOnly || organization.is_admin).map(tab => {
                        const Icon = tab.icon;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => {
                              setActiveTab(tab.id);
                              // Guest links live under Channels — load them with it.
                              if (tab.id === 'channels') loadGuestInvites();
                            }}
                            className={`flex items-center gap-2.5 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === tab.id
                              ? 'bg-gray-900 text-white'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                              }`}
                          >
                            <Icon className="w-4 h-4 shrink-0" />
                            {tab.label}
                          </button>
                        );
                      })}
                    </nav>

                  {/* Tab Content */}
                  <div className="space-y-6 sm:space-y-8 min-w-0">
                    {activeTab === 'info' && (
                      <div className="space-y-8">
                        {/* Hero header — the avatar IS the company logo (shown in customer
                            email reports); admins change it right here. */}
                        <div className="flex items-center gap-4 sm:gap-5">
                          <div className="relative shrink-0 group">
                            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden ${logoUrl ? 'bg-white border border-gray-200' : 'bg-gray-900 text-white'}`}>
                              {logoUrl ? (
                                <img
                                  src={logoUrl}
                                  alt={t('organization.logo.title', 'Company Logo')}
                                  className="w-full h-full object-contain p-1.5"
                                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              ) : (
                                <Building2 className="w-9 h-9" />
                              )}
                            </div>
                            {organization.is_admin && (
                              <label
                                className={`absolute inset-0 rounded-2xl flex items-center justify-center bg-gray-900/60 text-white transition-opacity ${isUploadingLogo ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100 cursor-pointer'}`}
                                title={t('organization.logo.upload', 'Upload Logo')}
                              >
                                {isUploadingLogo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                  className="sr-only"
                                  onChange={handleLogoFileChange}
                                  disabled={isUploadingLogo}
                                />
                              </label>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h2 className="text-xl font-semibold text-gray-900 truncate">{organization.name}</h2>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {organization.alias && (
                                <span className="text-sm text-gray-600 font-mono">{organization.alias}</span>
                              )}
                              {organization.is_admin && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-brand-100 text-brand-700 text-xs font-medium rounded-full">
                                  <Crown className="w-3 h-3" />
                                  {t('organization.admin', 'Admin')}
                                </span>
                              )}
                            </div>
                            {organization.is_admin && (
                              <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                                <label className={`inline-flex items-center gap-1.5 text-xs font-medium ${isUploadingLogo ? 'opacity-50 cursor-not-allowed' : 'text-brand-700 hover:underline cursor-pointer'}`}>
                                  <Upload className="w-3.5 h-3.5" />
                                  {logoUrl ? t('organization.logo.change', 'Change logo') : t('organization.logo.upload', 'Upload Logo')}
                                  <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                    className="sr-only"
                                    onChange={handleLogoFileChange}
                                    disabled={isUploadingLogo}
                                  />
                                </label>
                                {logoUrl && (
                                  <button
                                    type="button"
                                    onClick={handleDeleteLogo}
                                    disabled={isUploadingLogo}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    {t('organization.logo.remove', 'Remove')}
                                  </button>
                                )}
                                <span className="text-xs text-gray-500">
                                  {t('organization.logo.heroHint', 'Shown in email reports to your customers · PNG, JPEG, WebP or SVG, max 5 MB')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Details grid */}
                        <section>
                          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
                            {t('organization.details', 'Organization Details')}
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="bg-white rounded-xl border border-gray-200 p-5">
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                {t('organization.name', 'Name')}
                              </p>
                              <p className="text-base text-gray-900 font-medium break-words">{organization.name}</p>
                            </div>
                            {organization.alias && (
                              <div className="bg-white rounded-xl border border-gray-200 p-5">
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                  {t('organization.alias', 'Alias')}
                                </p>
                                <p className="text-base text-gray-900 font-mono break-words">{organization.alias}</p>
                              </div>
                            )}
                            <div className="bg-white rounded-xl border border-gray-200 p-5">
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                {t('organization.yourRole', 'Your Role')}
                              </p>
                              <p className="text-base text-gray-900 capitalize">{organization.role}</p>
                            </div>
                            {organization.description && (
                              <div className="bg-white rounded-xl border border-gray-200 p-5 sm:col-span-2">
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                  {t('organization.description', 'Description')}
                                </p>
                                <p className="text-base text-gray-900">{organization.description}</p>
                              </div>
                            )}
                          </div>
                        </section>

                        {/* Organization Settings Info */}
                        <section className="border-t border-gray-200 pt-6">
                          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
                            {t('organization.settingsInfo', 'Organization Settings')}
                          </h3>
                          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
                            <div className="flex items-start gap-3">
                              <Settings className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  {t('organization.settingsNote', 'Shared Catalog Configuration')}
                                </p>
                                <p className="text-sm text-brand-700">
                                  {organization.is_admin
                                    ? t('organization.adminSettingsNote', 'As an admin, you can configure catalog settings that will be shared with all organization members. Configure catalogs in the Catalog Settings section.')
                                    : t('organization.memberSettingsNote', 'Your organization administrator manages the catalog configurations. You can override these with personal settings if needed.')}
                                </p>
                              </div>
                            </div>
                          </div>
                        </section>
                      </div>
                    )}

                    {/* Language & currency — organization config, so it renders on
                        Info instead of behind a separate Settings tab. */}
                    {activeTab === 'info' && organization.is_admin && (
                      <div className="space-y-6 border-t border-gray-200 pt-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {t('organization.localeSettings.title', 'Language & Currency')}
                          </h3>
                          <p className="text-sm text-gray-600 mb-5">
                            {t('organization.localeSettings.description', 'Set the default language and currency for your organization. These settings apply to all members.')}
                          </p>

                          <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                            {/* Language */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <Globe className="w-4 h-4 text-gray-600" />
                                {t('organization.localeSettings.language', 'Language')}
                              </label>
                              <select
                                value={localeLanguage}
                                onChange={(e) => setLocaleLanguage(e.target.value)}
                                disabled={isSavingLocale}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              >
                                <option value="en">🇺🇸 {t('language.english', 'English')}</option>
                                <option value="es">🇪🇸 {t('language.spanish', 'Español')}</option>
                                <option value="es-MX">🇲🇽 {t('language.spanishMexican', 'Español (México)')}</option>
                                <option value="es-CL">🇨🇱 {t('language.spanishChilean', 'Español (Chile)')}</option>
                                <option value="es-EC">🇪🇨 {t('language.spanishEcuadorian', 'Español (Ecuador)')}</option>
                                <option value="pt">🇵🇹 {t('language.portuguese', 'Português')}</option>
                              </select>
                            </div>

                            {/* Currency */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <DollarSign className="w-4 h-4 text-gray-600" />
                                {t('organization.localeSettings.currency', 'Currency')}
                              </label>
                              <select
                                value={localeCurrency}
                                onChange={(e) => setLocaleCurrency(e.target.value)}
                                disabled={isSavingLocale}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              >
                                <option value="USD">🇺🇸 USD — US Dollar</option>
                                <option value="EUR">🇪🇺 EUR — Euro</option>
                                <option value="GBP">🇬🇧 GBP — British Pound</option>
                                <option value="MXN">🇲🇽 MXN — Mexican Peso</option>
                                <option value="BRL">🇧🇷 BRL — Brazilian Real</option>
                                <option value="ARS">🇦🇷 ARS — Argentine Peso</option>
                                <option value="CLP">🇨🇱 CLP — Chilean Peso</option>
                                <option value="COP">🇨🇴 COP — Colombian Peso</option>
                                <option value="PEN">🇵🇪 PEN — Peruvian Sol</option>
                                <option value="CAD">🇨🇦 CAD — Canadian Dollar</option>
                                <option value="AUD">🇦🇺 AUD — Australian Dollar</option>
                              </select>
                            </div>

                            {/* VAT Rate */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <span className="text-gray-600 font-semibold text-xs">%</span>
                                {t('organization.localeSettings.vatRate', 'VAT Rate (%)')}
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={vatRate}
                                onChange={(e) => setVatRate(e.target.value)}
                                disabled={isSavingLocale}
                                placeholder={t('organization.localeSettings.vatRatePlaceholder', 'e.g. 20 for 20% VAT (leave blank to hide)')}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              />
                              <p className="mt-1 text-xs text-gray-500">{t('organization.localeSettings.vatRateHint', 'When set, VAT and total incl. VAT will appear on quote PDFs.')}</p>
                            </div>

                            {/* Number Format */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <span className="text-gray-600 font-semibold text-xs">123</span>
                                {t('organization.localeSettings.numberFormat', 'Number Format')}
                              </label>
                              <select
                                value={numberFormat}
                                onChange={(e) => setNumberFormat(e.target.value)}
                                disabled={isSavingLocale}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              >
                                <option value="comma">{t('organization.localeSettings.numberFormatComma', 'Comma separated — 1,000.00')}</option>
                                <option value="dot">{t('organization.localeSettings.numberFormatDot', 'Dot separated — 1.000,00')}</option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">{t('organization.localeSettings.numberFormatHint', 'Controls how numbers and prices are displayed across the app.')}</p>
                            </div>

                            {/* Order alerts — lifecycle SLA thresholds (ADR 0004). The admin
                                `order_sla` feature flag is a hard off-switch on top of this. */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <span className="text-gray-600 font-semibold text-xs">⚠</span>
                                {t('organization.localeSettings.orderSla', 'Order alerts (SLA)')}
                              </label>
                              <p className="mb-2 text-xs text-gray-500">{t('organization.localeSettings.orderSlaHint', 'When an untouched order gets an amber "needs you" strip in Order Management and a digest to its owner. Handovers, questions and system errors always show regardless.')}</p>
                              <label className="flex items-center gap-2 mb-2 text-sm text-gray-900 cursor-pointer select-none">
                                <input type="checkbox" checked={slaEnabled} onChange={(e) => setSlaEnabled(e.target.checked)} disabled={isSavingLocale} className="rounded border-gray-300 text-gray-900 focus:ring-gray-900/10" />
                                {t('organization.localeSettings.orderSlaEnabled', 'Show order alerts for this organisation')}
                              </label>
                              <div className={`space-y-2 ${slaEnabled ? '' : 'opacity-50'}`}>
                                {([
                                  ['orderSlaReview', 'Results Ready unreviewed for', slaReviewHours, setSlaReviewHours, 'hours'],
                                  ['orderSlaCart', 'In Cart not submitted for', slaCartHours, setSlaCartHours, 'hours'],
                                  ['orderSlaReminder', 'Remind the customer after', slaReminderHours, setSlaReminderHours, 'hours'],
                                  ['orderSlaEscalate', 'Escalate back to you after', slaEscalateHours, setSlaEscalateHours, 'hours'],
                                  ['orderSlaWindow', 'Ignore orders idle for more than', slaWindowDays, setSlaWindowDays, 'days'],
                                ] as [string, string, string, (v: string) => void, string][]).map(([key, label, value, setter, unit]) => (
                                  <div key={key} className="flex items-center gap-2">
                                    <span className="flex-1 text-sm text-gray-900 px-1">{t(`organization.localeSettings.${key}`, label)}</span>
                                    <input
                                      type="number" min={0.25} step={unit === 'days' ? 1 : 0.5}
                                      value={value}
                                      onChange={(e) => setter(e.target.value)}
                                      disabled={isSavingLocale || !slaEnabled}
                                      className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                                    />
                                    <span className="text-xs text-gray-500 w-10">{t(`organization.localeSettings.unit_${unit}`, unit)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Country */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <Globe className="w-4 h-4 text-gray-600" />
                                {t('organization.localeSettings.country', 'Country')}
                              </label>
                              <select
                                value={localeCountry}
                                onChange={(e) => setLocaleCountry(e.target.value)}
                                disabled={isSavingLocale}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              >
                                <option value="">{t('organization.localeSettings.countryPlaceholder', '— Select country —')}</option>
                                <option value="US">🇺🇸 United States</option>
                                <option value="GB">🇬🇧 United Kingdom</option>
                                <option value="CA">🇨🇦 Canada</option>
                                <option value="AU">🇦🇺 Australia</option>
                                <option value="DE">🇩🇪 Germany</option>
                                <option value="FR">🇫🇷 France</option>
                                <option value="ES">🇪🇸 Spain</option>
                                <option value="IT">🇮🇹 Italy</option>
                                <option value="NL">🇳🇱 Netherlands</option>
                                <option value="PT">🇵🇹 Portugal</option>
                                <option value="MX">🇲🇽 Mexico</option>
                                <option value="BR">🇧🇷 Brazil</option>
                                <option value="AR">🇦🇷 Argentina</option>
                                <option value="CL">🇨🇱 Chile</option>
                                <option value="CO">🇨🇴 Colombia</option>
                                <option value="PE">🇵🇪 Peru</option>
                                <option value="EC">🇪🇨 Ecuador</option>
                                <option value="IL">🇮🇱 Israel</option>
                                <option value="IN">🇮🇳 India</option>
                                <option value="JP">🇯🇵 Japan</option>
                                <option value="KR">🇰🇷 South Korea</option>
                                <option value="ZA">🇿🇦 South Africa</option>
                              </select>
                            </div>

                            <button
                              onClick={() => handleSaveLocale()}
                              disabled={isSavingLocale}
                              className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 transition"
                            >
                              {isSavingLocale ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Save className="w-4 h-4" />
                              )}
                              {isSavingLocale
                                ? t('organization.localeSettings.saving', 'Saving...')
                                : t('organization.localeSettings.save', 'Save Settings')}
                            </button>
                          </div>
                        </div>


                      </div>
                    )}

                    {/* Warehouses — the org's stock locations, plus the cart stock/price and
                        delivery settings that depend on where stock sits. Saved together with
                        the locale form (same /settings payload). */}
                    {activeTab === 'warehouses' && organization.is_admin && (
                      <div className="space-y-8">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {t('organization.warehouses.title', 'Warehouses')}
                          </h3>
                          <p className="text-sm text-gray-600 mb-5">
                            {t('organization.warehouses.description', 'The stock locations your organization operates. The warehouse ID must match the location code your DMS reports (e.g. Codigo_Bodega); member visibility rules pick from this list.')}
                          </p>
                          <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                            <button
                              type="button"
                              onClick={() => setWarehousesOpen(o => !o)}
                              aria-expanded={warehousesOpen}
                              className="flex items-center gap-1.5 px-1 text-sm font-medium text-gray-700 hover:text-gray-900"
                            >
                              <ChevronDown className={`w-4 h-4 transition-transform ${warehousesOpen ? '' : '-rotate-90'}`} />
                              {t('organization.warehouses.count', '{{n}} warehouses', { n: warehouses.length })}
                            </button>
                            {warehousesOpen && (<>
                            <div className="hidden sm:grid grid-cols-[1fr_7rem_9rem_2rem] gap-2 px-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                              <span>{t('organization.warehouses.name', 'City / name')}</span>
                              <span>{t('organization.warehouses.abbrev', 'Abbrev.')}</span>
                              <span>{t('organization.warehouses.code', 'Warehouse ID')}</span>
                              <span />
                            </div>
                            {warehouses.length === 0 && (
                              <p className="px-1 py-2 text-sm text-gray-500">{t('organization.warehouses.none', 'No warehouses defined yet.')}</p>
                            )}
                            {warehouses.map((w, i) => {
                              const dup = !!w.code.trim() && warehouses.some((o, j) => j < i && o.code.trim().toUpperCase() === w.code.trim().toUpperCase());
                              const cell = 'px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60';
                              return (
                                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_7rem_9rem_2rem] gap-2 items-center">
                                  <input
                                    type="text"
                                    value={w.name}
                                    onChange={e => patchWarehouse(i, { name: e.target.value })}
                                    disabled={isSavingLocale}
                                    placeholder={t('organization.warehouses.namePlaceholder', 'e.g. Bogotá')}
                                    className={cell}
                                  />
                                  <input
                                    type="text"
                                    value={w.abbrev}
                                    onChange={e => patchWarehouse(i, { abbrev: e.target.value.toUpperCase() })}
                                    disabled={isSavingLocale}
                                    maxLength={8}
                                    placeholder="BOG"
                                    className={`${cell} font-mono uppercase`}
                                  />
                                  <input
                                    type="text"
                                    value={w.code}
                                    onChange={e => patchWarehouse(i, { code: e.target.value })}
                                    disabled={isSavingLocale}
                                    placeholder="PV412"
                                    aria-invalid={dup || undefined}
                                    title={dup ? t('organization.warehouses.duplicate', 'Duplicate warehouse ID') : undefined}
                                    className={`${cell} font-mono ${dup ? 'border-red-400 focus:ring-red-200' : ''}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setWarehouses(ws => ws.filter((_, j) => j !== i))}
                                    disabled={isSavingLocale}
                                    className="justify-self-end sm:justify-self-auto p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                                    title={t('organization.warehouses.remove', 'Remove warehouse')}
                                    aria-label={t('organization.warehouses.remove', 'Remove warehouse')}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })}
                            </>)}
                            <div className="flex flex-wrap items-center gap-4">
                            <button
                              type="button"
                              onClick={() => { setWarehouses(ws => [...ws, { ...EMPTY_WAREHOUSE }]); setWarehousesOpen(true); }}
                              disabled={isSavingLocale}
                              className="flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline disabled:opacity-50"
                            >
                              <Plus className="w-4 h-4" />
                              {t('organization.warehouses.add', 'Add warehouse')}
                            </button>
                            <label
                              className={`flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline cursor-pointer ${isSavingLocale ? 'opacity-50 pointer-events-none' : ''}`}
                              title={t('organization.warehouses.csvHint', 'Columns: name, abbrev, code (e.g. Codigo_Bodega). Rows with an existing code update it.')}
                            >
                              <Upload className="w-4 h-4" />
                              {t('organization.warehouses.importCsv', 'Import CSV')}
                              <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleWarehouseCsv} disabled={isSavingLocale} />
                            </label>
                            </div>
                          </div>
                        </div>

                        <div className="border-t border-gray-200 pt-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {t('organization.warehouses.cartTitle', 'Stock, price & delivery')}
                          </h3>
                          <p className="text-sm text-gray-600 mb-5">
                            {t('organization.warehouses.cartDescription', 'How stock and prices from your warehouses show up in the cart, and the delivery time quoted next to In Stock parts.')}
                          </p>
                          <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                            {/* Cart availability: source priority */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <span className="text-gray-600 font-semibold text-xs">◧</span>
                                {t('organization.localeSettings.inventoryPriority', 'Cart stock/price source')}
                              </label>
                              <select
                                value={inventorySourcePriority}
                                onChange={(e) => setInventorySourcePriority(e.target.value)}
                                disabled={isSavingLocale}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              >
                                <option value="dms_first">{t('organization.localeSettings.inventoryPriorityDmsFirst', 'DMS first, then importer')}</option>
                                <option value="importer_first">{t('organization.localeSettings.inventoryPriorityImporterFirst', 'Importer first, then DMS')}</option>
                                <option value="dms_only">{t('organization.localeSettings.inventoryPriorityDmsOnly', 'DMS only')}</option>
                                <option value="importer_only">{t('organization.localeSettings.inventoryPriorityImporterOnly', 'Importer only')}</option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">{t('organization.localeSettings.inventoryPriorityHint', 'Which integration source drives the cart stock/price tag when both are present.')}</p>
                            </div>

                            {/* Cart pricing: default price line-type (new vs alternative) */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <span className="text-gray-600 font-semibold text-xs">◧</span>
                                {t('organization.localeSettings.inventoryPricePriority', 'Default price shown')}
                              </label>
                              <select
                                value={inventoryPricePriority}
                                onChange={(e) => setInventoryPricePriority(e.target.value)}
                                disabled={isSavingLocale}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              >
                                <option value="original_first">{t('organization.localeSettings.inventoryPricePriorityOriginalFirst', 'Original part first (when in stock)')}</option>
                                <option value="alternative_first">{t('organization.localeSettings.inventoryPricePriorityAltFirst', 'Alternative part first (when in stock)')}</option>
                                <option value="lowest_available_first">{t('organization.localeSettings.inventoryPricePriorityLowest', 'Lowest available price first (in stock)')}</option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">{t('organization.localeSettings.inventoryPricePriorityHint', 'When a part has both an “original” and an “alternative” price, this decides which one shows and drives the quote by default. Users can still switch per line.')}</p>
                            </div>

                            {/* Default warehouse policy — which point of sale prices a line when
                                the part sits in several. Member home warehouses are set on Members. */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <Warehouse className="w-4 h-4 text-gray-600" />
                                {t('organization.localeSettings.locationPriority', 'Default warehouse for pricing')}
                              </label>
                              <select
                                value={inventoryLocationPriority}
                                onChange={(e) => setInventoryLocationPriority(e.target.value)}
                                disabled={isSavingLocale}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              >
                                <option value="list_order">{t('organization.localeSettings.locationPriorityListOrder', 'First warehouse returned by the DMS (list order)')}</option>
                                <option value="home_then_lowest">{t('organization.localeSettings.locationPriorityHomeLowest', 'Member’s home city (cheapest warehouse there), else the lowest in-stock price')}</option>
                                <option value="home_then_first">{t('organization.localeSettings.locationPriorityHomeFirst', 'Member’s home city (cheapest warehouse there), else the first warehouse with stock')}</option>
                                <option value="lowest">{t('organization.localeSettings.locationPriorityLowest', 'Always the lowest in-stock price')}</option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">{t('organization.localeSettings.locationPriorityHint', 'Decides which warehouse’s price a cart line starts from when a part is stocked in several. Each member’s home city is set on the Members tab; users can still switch warehouse per line.')}</p>
                            </div>

                            {/* Cart availability: show alternative price */}
                            <div>
                              <label className="flex items-center gap-2.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={inventoryShowAlternative}
                                  onChange={(e) => setInventoryShowAlternative(e.target.checked)}
                                  disabled={isSavingLocale}
                                  className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900/10 disabled:opacity-60"
                                />
                                <span className="text-sm font-medium text-gray-900">
                                  {t('organization.localeSettings.inventoryShowAlt', 'Show alternative part price')}
                                </span>
                              </label>
                              <p className="mt-1 ml-6 text-xs text-gray-500">{t('organization.localeSettings.inventoryShowAltHint', 'Include the DMS “alternative” line-type + price in the cart availability panel.')}</p>
                            </div>

                            {/* Cart availability: importer stock as a count vs In/Out of Stock */}
                            <div>
                              <label className="flex items-center gap-2.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={inventoryShowImporterQty}
                                  onChange={(e) => setInventoryShowImporterQty(e.target.checked)}
                                  disabled={isSavingLocale}
                                  className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900/10 disabled:opacity-60"
                                />
                                <span className="text-sm font-medium text-gray-900">
                                  {t('organization.localeSettings.inventoryShowImporterQty', 'Show importer stock quantity')}
                                </span>
                              </label>
                              <p className="mt-1 ml-6 text-xs text-gray-500">{t('organization.localeSettings.inventoryShowImporterQtyHint', 'Show the number of units the importer has. Off: only In Stock / Out of Stock.')}</p>
                            </div>

                            {/* Delivery time — zone table (spec §6.2). Shown on In Stock parts when the
                                `delivery_time` feature flag is enabled for this org (admin panel). */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <span className="text-gray-600 font-semibold text-xs">◧</span>
                                {t('organization.localeSettings.deliveryZones', 'Delivery time by zone')}
                              </label>
                              <p className="mb-2 text-xs text-gray-500">{t('organization.localeSettings.deliveryZonesHint', 'Estimated delivery time (in days) shown next to In Stock parts. Requires the “delivery_time” feature to be enabled for this organisation.')}</p>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={deliveryHomeCityLabel}
                                    onChange={(e) => setDeliveryHomeCityLabel(e.target.value)}
                                    disabled={isSavingLocale}
                                    placeholder={t('organization.localeSettings.deliveryHomeCity', 'Home city')}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                                  />
                                  <input
                                    type="number" min={0}
                                    value={deliveryHomeCityDays}
                                    onChange={(e) => setDeliveryHomeCityDays(e.target.value)}
                                    disabled={isSavingLocale}
                                    className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                                  />
                                  <span className="text-xs text-gray-500 w-10">{t('organization.localeSettings.deliveryDaysUnit', 'days')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="flex-1 text-sm text-gray-900 px-1">{t('organization.localeSettings.deliveryOutside', 'Outside (rest of country)')}</span>
                                  <input
                                    type="number" min={0}
                                    value={deliveryOutsideDays}
                                    onChange={(e) => setDeliveryOutsideDays(e.target.value)}
                                    disabled={isSavingLocale}
                                    className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                                  />
                                  <span className="text-xs text-gray-500 w-10">{t('organization.localeSettings.deliveryDaysUnit', 'days')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="flex-1 text-sm text-gray-900 px-1">{t('organization.localeSettings.deliveryInternational', 'International')}</span>
                                  <input
                                    type="number" min={0}
                                    value={deliveryInternationalDays}
                                    onChange={(e) => setDeliveryInternationalDays(e.target.value)}
                                    disabled={isSavingLocale}
                                    className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                                  />
                                  <span className="text-xs text-gray-500 w-10">{t('organization.localeSettings.deliveryDaysUnit', 'days')}</span>
                                </div>
                                <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                                  <span className="flex-1 text-sm font-medium text-gray-900 px-1">{t('organization.localeSettings.deliveryDefault', 'Default (unresolved location)')}</span>
                                  <input
                                    type="number" min={0}
                                    value={deliveryDefaultDays}
                                    onChange={(e) => setDeliveryDefaultDays(e.target.value)}
                                    disabled={isSavingLocale}
                                    className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                                  />
                                  <span className="text-xs text-gray-500 w-10">{t('organization.localeSettings.deliveryDaysUnit', 'days')}</span>
                                </div>
                              </div>
                              <p className="mt-2 text-xs text-gray-500">{t('organization.localeSettings.deliveryPerCustomerHint', 'A per-customer manual override (set on a B2B customer) always takes precedence over these zone values.')}</p>
                            </div>

                            <button
                              onClick={() => handleSaveLocale(t('organization.warehouses.saved', 'Warehouse settings updated successfully'))}
                              disabled={isSavingLocale}
                              className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 transition"
                            >
                              {isSavingLocale ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Save className="w-4 h-4" />
                              )}
                              {isSavingLocale
                                ? t('organization.localeSettings.saving', 'Saving...')
                                : t('organization.localeSettings.save', 'Save Settings')}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'members' && organization.is_admin && (                      <div className="space-y-6">
                        {/* Members List */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {t('organization.members', 'Members')} ({members.length})
                          </h3>
                          {members.length === 0 ? (
                            <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
                              <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                              <p className="text-sm text-gray-600">
                                {t('organization.noMembers', 'No members yet')}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {members.map((member) => (
                                <div
                                  key={member.uid}
                                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                                >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                      <User className="w-5 h-5 text-gray-600" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{member.name}</p>
                                      <p className="text-xs text-gray-600">{member.email}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() => setRulesFor(rulesFor === member.uid ? null : member.uid)}
                                      className={`px-3 py-1 text-xs font-medium rounded-full ${member.permissions?.rules?.length ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-900'} ${rulesFor === member.uid ? 'ring-2 ring-gray-300' : ''}`}
                                      title={t('organization.rules.title', 'Visibility rules — what this member can see')}
                                    >
                                      {(member.permissions?.home_city || member.permissions?.home_warehouse) && (
                                        <span className="mr-1.5">{member.permissions.home_city || member.permissions.home_warehouse}</span>
                                      )}
                                      {member.permissions?.rules?.length
                                        ? t('organization.rules.count', '{{n}} rules', { n: member.permissions.rules.length })
                                        : t('organization.rules.noLimits', 'No limits')}
                                    </button>
                                    <span
                                      className={`px-3 py-1 text-xs font-medium rounded-full ${member.role === 'admin'
                                        ? 'bg-brand-100 text-brand-700'
                                        : 'bg-gray-100 text-gray-900'
                                        }`}
                                    >
                                      {member.role === 'admin' ? (
                                        <span className="flex items-center gap-1">
                                          <Crown className="w-3 h-3" />
                                          {t('organization.admin', 'Admin')}
                                        </span>
                                      ) : (
                                        t('organization.member', 'Member')
                                      )}
                                    </span>
                                    <button
                                      onClick={() => handleRemoveMember(member.uid)}
                                      disabled={isSaving}
                                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={t('organization.removeMember', 'Remove member')}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                {rulesFor === member.uid && (
                                  <MemberRules member={member} warehouses={warehouses} onSave={handleSetMemberRules} />
                                )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {activeTab === 'channels' && organization.is_admin && (
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {t('organization.channels.title', 'Channels')}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {t('organization.channels.description', 'Configure organisation-level communication channels')}
                          </p>
                        </div>

                        {/* WhatsApp */}
                        <div className="rounded-xl border border-gray-200 overflow-hidden">
                          <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-white">
                            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                              <MessageCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">WhatsApp</p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {t('organization.channels.whatsapp.description', 'Receive parts requests over WhatsApp and reply from CATA.')}
                              </p>
                            </div>
                          </div>
                          <div className="p-4 bg-gray-50">
                            <OrgWhatsAppPanel />
                          </div>
                        </div>

                        {/* Email — Mailbox Configuration */}
                        <div className="rounded-xl border border-gray-200 overflow-hidden">
                          <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-white">
                            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                              <Mail className="w-5 h-5 text-brand-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{t('organization.mailbox.title', 'Mailbox Configuration')}</p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {t('organization.mailbox.description', 'Forward or send emails to your dedicated address for automated processing.')}
                              </p>
                            </div>
                          </div>
                          <div className="p-4 bg-gray-50 space-y-4">
                            {/* Inbound address (read-only) */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <Mail className="w-4 h-4 text-gray-600" />
                                {t('organization.mailbox.inboundAddress', 'Your Inbound Email Address')}
                              </label>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white font-mono text-gray-900 select-all">
                                  auto+{organization.alias}@catax.ai
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const addr = `auto+${organization.alias}@catax.ai`;
                                    navigator.clipboard.writeText(addr);
                                    showNotification(t('organization.success', 'Success'), t('organization.mailbox.copied', 'Address copied to clipboard'), 'success');
                                  }}
                                  className="p-2.5 rounded-lg border border-gray-200 bg-white hover:border-gray-400 text-gray-600 hover:text-gray-900 transition"
                                  title={t('organization.mailbox.copy', 'Copy address')}
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <p className="mt-1.5 text-xs text-gray-500">
                                {t('organization.mailbox.inboundHint', "Send or forward emails to this address and they will be automatically processed under your organization's catalog.")}
                              </p>
                            </div>

                            {/* Reply-to email */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                                <Mail className="w-4 h-4 text-gray-600" />
                                {t('organization.mailbox.replyEmail', 'Reply-To Email')}
                              </label>
                              <input
                                type="email"
                                value={replyEmail}
                                onChange={(e) => setReplyEmail(e.target.value)}
                                disabled={isSavingLocale}
                                placeholder={t('organization.mailbox.replyEmailPlaceholder', 'e.g. results@yourcompany.com (leave empty to reply to sender)')}
                                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 bg-white disabled:opacity-60"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                {t('organization.mailbox.replyEmailHint', 'When set, processing results will be sent to this address instead of the original sender.')}
                              </p>
                            </div>

                            <button
                              onClick={() => handleSaveLocale()}
                              disabled={isSavingLocale}
                              className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 transition"
                            >
                              {isSavingLocale ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Save className="w-4 h-4" />
                              )}
                              {isSavingLocale
                                ? t('organization.localeSettings.saving', 'Saving...')
                                : t('organization.localeSettings.save', 'Save Settings')}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Guest Access — a public way into the organization, so it lives
                        with the other inbound channels (WhatsApp, mailbox). ───────── */}
                    {activeTab === 'channels' && organization.is_admin && (
                      <div className="space-y-6 border-t border-gray-200 pt-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {t('organization.guest.title', 'Guest Access Links')}
                          </h3>
                          <p className="text-sm text-gray-600 mb-5">
                            {t('organization.guest.description', 'Generate shareable links that let anyone search parts under your organization\'s catalog — no login required.')}
                          </p>

                          {/* Create invite form */}
                          <details className="mb-6 group">
                            <summary className="flex items-center justify-between cursor-pointer list-none select-none bg-gray-50 rounded-xl px-4 py-3 group-open:rounded-b-none group-open:border-b border-gray-200">
                              <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                <Plus className="w-4 h-4 text-gray-500" />
                                {t('organization.guest.newLink', 'New Invite Link')}
                              </span>
                              <div className="flex items-center gap-2">
                                {guestInvites.some(i => i.is_active) && (
                                  <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                    {t('organization.guest.oneLinkLimit', 'Revoke the active link to create a new one')}
                                  </span>
                                )}
                                <svg className="w-4 h-4 text-gray-500 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                              </div>
                            </summary>
                          <div className={`bg-gray-50 rounded-b-xl px-4 pb-4 pt-3 space-y-3 ${guestInvites.some(i => i.is_active) ? 'opacity-60 pointer-events-none' : ''}`}>
                            <input
                              type="text"
                              value={newInviteLabel}
                              onChange={e => setNewInviteLabel(e.target.value)}
                              placeholder={t('organization.guest.labelPlaceholder', 'Label (optional, e.g. "Customer Demo")')}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                            />
                            <input
                              type="text"
                              value={newInviteHeaderText}
                              onChange={e => setNewInviteHeaderText(e.target.value)}
                              placeholder={t('organization.guest.headerTextPlaceholder', 'Page header (optional, e.g. "Welcome to Acme Parts")')}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                            />
                            <input
                              type="text"
                              value={newInviteAllowedDomain}
                              onChange={e => setNewInviteAllowedDomain(e.target.value)}
                              placeholder={t('organization.guest.allowedDomainPlaceholder', 'Allowed domain (optional, e.g. "example.com")')}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                            />
                            {/* Icon: file upload OR URL */}
                            <div className="space-y-1.5">
                              <p className="text-xs text-gray-600">{t('organization.guest.iconLabel', 'Icon / Logo (optional)')}</p>
                              <div className="flex gap-2 items-center">
                                {/* Preview */}
                                {newInviteIconUrl ? (
                                  <div className="relative flex-shrink-0">
                                    <img
                                      src={newInviteIconUrl}
                                      alt="icon preview"
                                      className="w-10 h-10 rounded-lg object-contain border border-gray-200 bg-white"
                                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setNewInviteIconUrl('')}
                                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-900 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition"
                                    >
                                      <X className="w-2.5 h-2.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-white hover:border-gray-400 cursor-pointer transition">
                                    {isUploadingIcon
                                      ? <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                                      : <Plus className="w-4 h-4 text-gray-500" />}
                                    <input
                                      type="file"
                                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                                      className="hidden"
                                      onChange={handleIconFileChange}
                                      disabled={isUploadingIcon}
                                    />
                                  </label>
                                )}
                                <input
                                  type="url"
                                  value={newInviteIconUrl}
                                  onChange={e => setNewInviteIconUrl(e.target.value)}
                                  placeholder={t('organization.guest.iconUrlPlaceholder', 'Or paste an image URL…')}
                                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                                />
                              </div>
                            </div>
                            <div className="flex gap-3">
                              <input
                                type="number"
                                min={1}
                                value={newInviteMaxUses}
                                onChange={e => setNewInviteMaxUses(e.target.value)}
                                placeholder={t('organization.guest.maxUsesPlaceholder', 'Max uses (leave empty = unlimited)')}
                                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                              />
                              <input
                                type="number"
                                min={1}
                                value={newInviteExpiresHours}
                                onChange={e => setNewInviteExpiresHours(e.target.value)}
                                placeholder={t('organization.guest.expiresPlaceholder', 'Expires in hours (optional)')}
                                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                              />
                            </div>
                            <button
                              onClick={handleCreateInvite}
                              disabled={isCreatingInvite || guestInvites.some(i => i.is_active)}
                              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 transition"
                            >
                              {isCreatingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                              {t('organization.guest.generate', 'Generate Link')}
                            </button>
                          </div>
                          </details>

                          {/* Invite list header with active-only toggle */}
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-900">{t('organization.guest.links', 'Invite Links')}</p>
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <span className="text-xs text-gray-600">{t('organization.guest.activeOnly', 'Active only')}</span>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={showActiveOnly}
                                onClick={() => setShowActiveOnly(v => !v)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showActiveOnly ? 'bg-gray-900' : 'bg-gray-200'}`}
                              >
                                <span
                                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${showActiveOnly ? 'translate-x-[18px]' : 'translate-x-1'}`}
                                />
                              </button>
                            </label>
                          </div>
                          {guestInvitesLoading ? (
                            <div className="flex justify-center py-8">
                              <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                            </div>
                          ) : guestInvites.filter(i => !showActiveOnly || i.is_active).length === 0 ? (
                            <div className="text-center py-10 bg-white border border-gray-200 rounded-xl">
                              <Link className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                              <p className="text-sm text-gray-500">
                                {guestInvites.length === 0
                                  ? t('organization.guest.noLinks', 'No invite links yet. Generate one above.')
                                  : t('organization.guest.noActiveLinks', 'No active links. Toggle the filter to see revoked links.')}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {guestInvites.filter(i => !showActiveOnly || i.is_active).map(invite => (
                                <div
                                  key={invite.token}
                                  className={`flex flex-col gap-2 p-4 rounded-xl border min-w-0 overflow-hidden ${invite.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      {invite.icon_url && (
                                        <img
                                          src={invite.icon_url}
                                          alt=""
                                          className="w-8 h-8 rounded object-contain border border-gray-200 bg-white flex-shrink-0"
                                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {invite.label || t('organization.guest.unnamedLink', 'Unnamed link')}
                                        </p>
                                        {invite.header_text && (
                                          <p className="text-xs text-gray-600 truncate">{invite.header_text}</p>
                                        )}
                                        <p className="text-xs text-gray-500 font-mono mt-0.5 truncate">
                                          {guestLink(invite.token)}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${invite.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                        {invite.is_active ? t('organization.guest.active', 'Active') : t('organization.guest.revoked', 'Revoked')}
                                      </span>
                                      {invite.is_active && (
                                        <>
                                          <button
                                            onClick={() => copyInviteLink(invite.token)}
                                            title={t('organization.guest.copyLink', 'Copy link')}
                                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition"
                                          >
                                            {copiedToken === invite.token
                                              ? <CheckCircle className="w-4 h-4 text-green-600" />
                                              : <Copy className="w-4 h-4" />}
                                          </button>
                                          <a
                                            href={guestLink(invite.token)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={t('organization.guest.openLink', 'Open link')}
                                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition"
                                          >
                                            <ExternalLink className="w-4 h-4" />
                                          </a>
                                          <button
                                            onClick={() => handleRevokeInvite(invite.token)}
                                            title={t('organization.guest.revokeLink', 'Revoke')}
                                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-4 text-xs text-gray-500">
                                    <span>{t('organization.guest.uses', 'Uses')}: {invite.use_count}{invite.max_uses ? ` / ${invite.max_uses}` : ''}</span>
                                    {invite.expires_at && (
                                      <span>{t('organization.guest.expires', 'Expires')}: {new Date(invite.expires_at).toLocaleDateString()}</span>
                                    )}
                                    <span>{t('organization.guest.created', 'Created')}: {new Date(invite.created_at).toLocaleDateString()}</span>
                                    {invite.allowed_domain && (
                                      <span className="flex items-center gap-1">
                                        <Globe className="w-3 h-3" />
                                        {invite.allowed_domain}
                                      </span>
                                    )}
                                  </div>

                                  {/* Direct link */}
                                  {invite.is_active && (
                                    <div className="mt-1 flex items-center gap-2 min-w-0">
                                      <span className="text-xs text-gray-500 flex-shrink-0">{t('organization.guest.directLink', 'Direct link:')}</span>
                                      <span className="text-xs font-mono text-gray-600 truncate flex-1 min-w-0">{guestLink(invite.token)}</span>
                                      <button
                                        onClick={() => copyInviteLink(invite.token)}
                                        title={t('organization.guest.copyLink', 'Copy link')}
                                        className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition"
                                      >
                                        {copiedToken === invite.token
                                          ? <><CheckCircle className="w-3 h-3 text-green-600" />{t('organization.guest.copied', 'Copied!')}</>
                                          : <><Copy className="w-3 h-3" />{t('organization.guest.copy', 'Copy')}</>}
                                      </button>
                                    </div>
                                  )}

                                  {/* SDK embed snippet */}
                                  {invite.is_active && (
                                    <details className="mt-1 min-w-0">
                                      <summary className="text-xs text-indigo-600 cursor-pointer select-none hover:text-indigo-800 transition w-fit">
                                        {t('organization.guest.showEmbed', '</> Embed on your website')}
                                      </summary>
                                      <div className="mt-2 relative min-w-0">
                                        <pre className="text-xs bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto leading-relaxed whitespace-pre w-full">{`<!-- 1. Load the Catax SDK (always latest) -->
<script src="https://cdn.catax.ai/sdk/latest/catax.min.js"></script>

<!-- 2. Initialise with your guest link -->
<script>
  new CataxSDK({
    guestLink: '${guestLink(invite.token)}',
    onEvent: (event) => console.log('catax event', event),
  });
</script>`}</pre>
                                        <button
                                          onClick={() => {
                                            const snippet = [
                                              '<!-- 1. Load the Catax SDK (always latest) -->',
                                              `<script src="https://cdn.catax.ai/sdk/latest/catax.min.js"></script>`,
                                              '',
                                              '<!-- 2. Initialise with your guest link -->',
                                              '<script>',
                                              '  new CataxSDK({',
                                              '    // ── Required ───────────────────────────────────────',
                                              `    guestLink: '${guestLink(invite.token)}',`,
                                              "    onEvent: (event) => console.log('catax event', event),",
                                              '  });',
                                              '</script>',
                                            ].join('\n');
                                            navigator.clipboard.writeText(snippet);
                                            setCopiedToken(`snippet-${invite.token}`);
                                            setTimeout(() => setCopiedToken(null), 2000);
                                          }}
                                          className="absolute top-2 right-2 p-1.5 rounded-md bg-gray-900 hover:bg-gray-800 text-gray-300 hover:text-white transition"
                                          title={t('organization.guest.copySnippet', 'Copy snippet')}
                                        >
                                          {copiedToken === `snippet-${invite.token}`
                                            ? <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                                            : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                      </div>
                                      <p className="mt-1.5 text-xs text-gray-500">
                                        {t('organization.guest.embedNote', 'Paste this snippet just before </body> on any HTML page. The SDK loads from the CDN and adds a floating button that opens a parts-search popup.')}
                                      </p>
                                    </details>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
    </div>
  );

  if (asModal) return innerContent;

  return (
    <AppLayout pageTitle={t('organization.title', 'Organization')}>
      {innerContent}
    </AppLayout>
  );
};

export default OrganizationSettingsPanel;
